/* eslint-disable no-nested-ternary */
import React, { memo, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useMaterialUIController } from "context";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";

import Icon from "components/CustomIcons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SensorReading from "./SensorReading";
import ModalDevice16 from "../DeviceModals/ModalDevice16";

const UNIT_MAP = {
  62: "ºC",
  29: "%",
  95: "",
  96: "ppm",
  97: "ppb",
  219: "µg/m³",
  63: "K",
  64: "°F",
  53: "Pa",
  54: "kPa",
  55: "bar",
  56: "psi",
  18: "Wh",
  19: "kWh",
  48: "kW",
  49: "MW",
  52: "TR",
  84: "CFM",
  85: "m³/s",
  88: "L/min",
  89: "GPM",
  70: "d",
  71: "h",
  72: "min",
  73: "s",
  75: "km/h",
  78: "mph",
};

const getColour = (value, normalizePoint, riskPoint, isPositive) => {
  if (value == null) return "grey";

  if (isPositive) {
    if (value < riskPoint) return "red";
    if (value >= riskPoint && value < normalizePoint) return "orange";
    if (value >= normalizePoint) return "green";
    return "grey";
  }

  if (value <= normalizePoint) return "green";
  if (value > normalizePoint && value < riskPoint) return "orange";
  if (value >= riskPoint) return "red";
  return "grey";
};

const formatValue = (value) => (value === null ? "--" : value);

function CardDevice16({ device, addressesMap }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [open, setOpen] = useState(false);

  const addrMap = useMemo(() => new Map(Object.entries(addressesMap)), [addressesMap]);

  const renderProp = (property) => {
    const address = addrMap.get(property?.address) || {};
    return (
      <SensorReading
        key={property?.address}
        label={property?.label || ""}
        value={formatValue(address?.value)}
        unit={property?.unit || UNIT_MAP[address?.unit] || ""}
        color={getColour(
          address?.value,
          property?.normalizePoint,
          property?.riskPoint,
          property?.isPositive
        )}
      />
    );
  };

  const hasSecondary = (device?.secondaryProperties || []).length > 0;

  return (
    <Card sx={{ height: "100%", overflow: "hidden" }}>
      <ModalDevice16
        open={open}
        onClose={() => setOpen(false)}
        device={device}
        addressesMap={addressesMap}
      />

      <MDBox
        p={2}
        height="100%"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <MDBox display="flex" flexWrap="wrap" gap={3} justifyContent="space-evenly" mb={2}>
          {device?.primaryProperties.map(renderProp)}
        </MDBox>

        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          {device?.name && (
            <MDTypography variant="button">{device?.name.toUpperCase()}</MDTypography>
          )}

          {hasSecondary && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              sx={{
                backgroundColor: darkMode ? "white" : "lightgray",
                borderRadius: "50%",
                p: 1,
                "&:hover": { backgroundColor: darkMode ? "white" : "lightgray" },
              }}
            >
              <Icon name="Plus" color={darkMode ? "gray" : "white"} size={28} />
            </IconButton>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

CardDevice16.propTypes = { device: PropTypes.object.isRequired, addressesMap: PropTypes.object };
CardDevice16.defaultProps = { addressesMap: {} };

const mapStateToProps = (state, { device }) => {
  const { bacnetAddresses = {}, knxAddressReducer = [] } = state;
  // const { knxAddressReducer = [] } = state;
  const propertyIds = [
    ...(device?.primaryProperties ?? []),
    ...(device?.secondaryProperties ?? []),
  ].map((prop) => prop?.address);

  const addressesMap = propertyIds.reduce((acc, id) => {
    const bacnet = bacnetAddresses[id];
    const knx = knxAddressReducer.find((address) => address?.id === id);
    acc[id] = bacnet || knx || null;
    // acc[id] = knx || null;
    return acc;
  }, {});

  return { addressesMap };
};

const areEqual = (prev, next) => {
  if (prev.device?.nodeId !== next.device?.nodeId) return false;

  const devicePropertiesIds = [
    ...(prev.device?.primaryProperties || []),
    ...(prev.device?.secondaryProperties || []),
  ].map((property) => property?.address);

  return !devicePropertiesIds.some(
    (propertyId) => prev.addressesMap[propertyId]?.value !== next.addressesMap[propertyId]?.value
  );
};

export default connect(mapStateToProps)(memo(CardDevice16, areEqual));
